import React, { createContext, useState, useCallback, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { Toast } from "flowbite-react";
import {
  HiCheck,
  HiExclamation,
  HiX,
  HiInformationCircle,
} from "react-icons/hi";
import { setGlobalToastHandler } from "./globalToastHandler.ts";

// Toast message type
interface ToastMessage {
  id: string;
  type: "info" | "warning" | "error" | "success";
  title?: string;
  message: string;
  duration?: number;
}

// Toast context type
interface ToastContextType {
  addInfoMessage: (message: string, title?: string, duration?: number) => void;
  addWarningMessage: (
    message: string,
    title?: string,
    duration?: number
  ) => void;
  addErrorMessage: (message: string, title?: string, duration?: number) => void;
  addSuccessMessage: (
    message: string,
    title?: string,
    duration?: number
  ) => void;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
  // Suppress incoming toasts (e.g. a stray backend/WebSocket notification)
  // while a blocking confirmation modal is on screen. Calls nest safely -
  // toasts resume only once every pauseToasts() has a matching resumeToasts().
  pauseToasts: () => void;
  resumeToasts: () => void;
}

// Create the toast context
export const ToastContext = createContext<ToastContextType | null>(null);

// Provider props
interface ToastProviderProps {
  children: ReactNode;
  position?:
    | "top-right"
    | "top-left"
    | "bottom-right"
    | "bottom-left"
    | "top-center"
    | "bottom-center";
  maxToasts?: number;
}

// Toast Provider component
export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = "top-right",
  maxToasts = 5,
}) => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  // Ref, not state: pause/resume must take effect synchronously (no waiting
  // for a re-render) so a toast fired right after resumeToasts() - e.g. a
  // mutation's own success message - is never dropped by a stale pause.
  const pauseCountRef = useRef(0);

  // Generate unique ID for toasts
  const generateId = useCallback(() => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }, []);

  // Remove specific toast
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const pauseToasts = useCallback(() => {
    pauseCountRef.current += 1;
  }, []);

  const resumeToasts = useCallback(() => {
    pauseCountRef.current = Math.max(0, pauseCountRef.current - 1);
  }, []);

  // Add toast message
  const addToast = useCallback(
    (
      type: ToastMessage["type"],
      message: string,
      title?: string,
      duration: number = 0
    ) => {
      // Drop toasts raised while paused (e.g. a stray WebSocket notification
      // arriving behind a delete confirmation modal) instead of queuing them -
      // showing them later, out of context, would be just as confusing.
      if (pauseCountRef.current > 0) {
        return;
      }

      const id = generateId();
      const newToast: ToastMessage = {
        id,
        type,
        message,
        title,
        duration,
      };

      setToasts((prev) => {
        const updatedToasts = [newToast, ...prev];
        // Limit number of toasts
        return updatedToasts.slice(0, maxToasts);
      });

      // Auto remove toast after duration
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }
    },
    [generateId, maxToasts, removeToast]
  );

  // Clear all toasts
  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Specific message type functions
  const addInfoMessage = useCallback(
    (message: string, title?: string, duration?: number) => {
      addToast("info", message, title, duration);
    },
    [addToast]
  );

  const addWarningMessage = useCallback(
    (message: string, title?: string, duration?: number) => {
      addToast("warning", message, title, duration);
    },
    [addToast]
  );

  const addErrorMessage = useCallback(
    (message: string, title?: string, duration?: number) => {
      addToast("error", message, title, duration);
    },
    [addToast]
  );

  const addSuccessMessage = useCallback(
    (message: string, title?: string, duration?: number) => {
      addToast("success", message, title, duration);
    },
    [addToast]
  );

  // Connect global toast handler for QueryClient mutations
  useEffect(() => {
    const handler = (message: string, type: "error" | "success" | "info") => {
      switch (type) {
        case "error":
          addErrorMessage(message);
          break;
        case "success":
          addSuccessMessage(message);
          break;
        case "info":
          addInfoMessage(message);
          break;
      }
    };

    setGlobalToastHandler(handler);

    // Cleanup on unmount
    return () => {
      setGlobalToastHandler(() => {});
    };
  }, [addErrorMessage, addSuccessMessage, addInfoMessage]);

  // Get toast icon and colors based on type
  const getToastConfig = (type: ToastMessage["type"]) => {
    switch (type) {
      case "success":
        return {
          icon: HiCheck,
          iconBg: "bg-green-100",
          iconColor: "text-green-500",
          borderColor: "border-green-200",
        };
      case "warning":
        return {
          icon: HiExclamation,
          iconBg: "bg-yellow-100",
          iconColor: "text-yellow-500",
          borderColor: "border-yellow-200",
        };
      case "error":
        return {
          icon: HiX,
          iconBg: "bg-red-100",
          iconColor: "text-red-500",
          borderColor: "border-red-200",
        };
      case "info":
      default:
        return {
          icon: HiInformationCircle,
          iconBg: "bg-blue-100",
          iconColor: "text-blue-500",
          borderColor: "border-blue-200",
        };
    }
  };

  // Position styles
  const getPositionStyles = () => {
    const baseStyles = "fixed z-50 flex flex-col gap-2 pointer-events-none";

    switch (position) {
      case "top-left":
        return `${baseStyles} top-4 left-4`;
      case "top-center":
        return `${baseStyles} top-4 left-1/2 transform -translate-x-1/2`;
      case "top-right":
        return `${baseStyles} top-4 right-4`;
      case "bottom-left":
        return `${baseStyles} bottom-4 left-4`;
      case "bottom-center":
        return `${baseStyles} bottom-4 left-1/2 transform -translate-x-1/2`;
      case "bottom-right":
        return `${baseStyles} bottom-4 right-4`;
      default:
        return `${baseStyles} top-4 right-4`;
    }
  };

  const value: ToastContextType = {
    addInfoMessage,
    addWarningMessage,
    addErrorMessage,
    addSuccessMessage,
    removeToast,
    clearAllToasts,
    pauseToasts,
    resumeToasts,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}

      {/* Toast Container */}
      <div className={getPositionStyles()}>
        {toasts.map((toast) => {
          const config = getToastConfig(toast.type);
          const IconComponent = config.icon;

          return (
            <div key={toast.id} className="pointer-events-auto">
              <Toast>
                <div
                  className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${config.iconBg} ${config.iconColor}`}
                >
                  <IconComponent className="h-5 w-5" />
                </div>
                <div className="ml-3 text-sm font-normal">
                  {toast.title && (
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {toast.title}
                    </div>
                  )}
                  <div
                    className={
                      toast.title
                        ? "text-gray-600 dark:text-gray-400"
                        : "text-gray-900 dark:text-white"
                    }
                  >
                    {toast.message}
                  </div>
                </div>
                <button
                  type="button"
                  className="ml-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
                  aria-label="Close"
                  onClick={() => removeToast(toast.id)}
                >
                  <span className="sr-only">Close</span>
                  <HiX className="w-5 h-5" />
                </button>
              </Toast>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
